/**
 * Per-app configuration for the Component Inspector.
 *
 * The inspector is a generic React component that walks fiber trees, reads
 * source files, and presents an overlay. The few app-specific bits (the file
 * tree to introspect, the project's directory layout, the storage namespace)
 * are injected once at app startup via `setInspectorConfig`. The inspector
 * reads from this module's state instead of having those values hardcoded.
 */

import type { ReactNode } from "react";

export interface InspectorSourceMap {
  /**
   * Map of source-file paths (e.g. "/src/Core/Button.tsx") to a loader that
   * returns the raw file contents. Produced by
   * `import.meta.glob("/src/**\/*.{ts,tsx}", { query: "?raw", import: "default" })`.
   * Its keys are also the inspector's file list. Never pass a plain
   * `import.meta.glob` of the modules themselves: each one becomes a dynamic
   * import, so the bundler splits every source file into its own chunk and
   * the app has to fetch them all before it can boot.
   */
  rawLoaders: Record<string, () => Promise<string>>;
}

export interface InspectorConfig {
  /**
   * Storage namespace prefix for inspector state. e.g. "hubble" → keys
   * "hubble:component-inspector", "hubble:component-inspector-key", etc.
   */
  storageNamespace: string;
  /** Absolute path of the project root (for editor:// links). */
  projectRoot: string;
  /**
   * Classify a source-file path into a category. Drives the inspector's
   * stack/owner-tree color coding. Default checks for `/Core/` (core),
   * `/Cargo/` (core), `/Features/` (feature), else external.
   */
  classifyPath?: (filePath: string) => "core" | "feature" | "external";
  /**
   * Component names that should be "looked through" when resolving the
   * focused component — e.g. wrapper components like `Stack`, `Dialog` that
   * don't add visible JSX a developer would care about.
   */
  transparentNames?: ReadonlySet<string>;
  /**
   * Component name that marks the boundary between app scaffold (above) and
   * feature code (below). Hubble uses "Router". Stack items at or above this
   * name in the React tree are filtered out of the inspector display.
   */
  scaffoldComponentName?: string;
  /**
   * Custom DOM event the inspector listens for to programmatically open a
   * file (e.g. fired by an error boundary). Hubble uses "hubble:open-file".
   */
  openFileEventName?: string;
  /** Children of the wrapper component, passed through unchanged. */
  children?: ReactNode;
}

let sourceMapState: InspectorSourceMap | undefined;
let configState: InspectorConfig | undefined;

export function setInspectorSourceMap(map: InspectorSourceMap): void {
  sourceMapState = map;
}

export function setInspectorConfig(config: InspectorConfig): void {
  configState = config;
}

export function getInspectorConfig(): InspectorConfig {
  if (!configState) {
    throw new Error(
      "Inspector config not set. Call setInspectorConfig() before rendering <ComponentInspector />.",
    );
  }
  return configState;
}

const DEFAULT_TRANSPARENT_NAMES = new Set<string>();

const DEFAULT_CLASSIFY: NonNullable<InspectorConfig["classifyPath"]> = (
  path,
) => {
  if (path.includes("/Core/") || path.includes("/Cargo/")) return "core";
  if (path.includes("/Features/")) return "feature";
  return "external";
};

export function classifyPath(
  filePath: string,
): "core" | "feature" | "external" {
  const fn = configState?.classifyPath ?? DEFAULT_CLASSIFY;
  return fn(filePath);
}

export function getTransparentNames(): ReadonlySet<string> {
  return configState?.transparentNames ?? DEFAULT_TRANSPARENT_NAMES;
}

const ENABLED_SUFFIX = ":component-inspector";
const KEY_SUFFIX = ":component-inspector-key";
const EDITOR_SUFFIX = ":component-inspector-editor";
// When `setInspectorConfig` hasn't been called (e.g. in unit-test environments
// that import the menu primitives without booting the inspector) we fall back
// to a generic "app:" prefix so localStorage reads/writes still work without
// throwing. Real apps always overwrite this by calling setInspectorConfig at
// startup.
const FALLBACK_NAMESPACE = "app";

function namespace(): string {
  return configState?.storageNamespace ?? FALLBACK_NAMESPACE;
}

export function getEnabledStorageKey(): string {
  return `${namespace()}${ENABLED_SUFFIX}`;
}

export function getActivationKeyStorageKey(): string {
  return `${namespace()}${KEY_SUFFIX}`;
}

export function getEditorStorageKey(): string {
  return `${namespace()}${EDITOR_SUFFIX}`;
}

interface SourcePathEntry {
  path: string;
  short: string;
  name: string;
}

let cachedComponentPathMap: Map<string, string[]> | undefined;
let cachedQuickOpenFiles: SourcePathEntry[] | undefined;

function getComponentPathMap(): Map<string, string[]> {
  if (!cachedComponentPathMap) {
    cachedComponentPathMap = new Map();
    if (sourceMapState) {
      for (const p of Object.keys(sourceMapState.rawLoaders)) {
        const name = p
          .split("/")
          .pop()
          ?.replace(/\.tsx?$/, "");
        if (name && !name.includes(".stories") && !name.includes(".test")) {
          const existing = cachedComponentPathMap.get(name) ?? [];
          existing.push(p);
          cachedComponentPathMap.set(name, existing);
        }
      }
    }
  }
  return cachedComponentPathMap;
}

export function lookupFilePath(componentName: string): string | undefined {
  const paths = getComponentPathMap().get(componentName);
  if (!paths || paths.length === 0) return undefined;
  return paths[0];
}

export async function loadSource(
  filePath: string,
): Promise<string | undefined> {
  if (!sourceMapState) return undefined;
  const loader = sourceMapState.rawLoaders[filePath];
  if (!loader) return undefined;
  return loader();
}

export function getQuickOpenFiles(): readonly SourcePathEntry[] {
  if (!cachedQuickOpenFiles) {
    if (!sourceMapState) {
      cachedQuickOpenFiles = [];
    } else {
      cachedQuickOpenFiles = Object.keys(sourceMapState.rawLoaders)
        .filter(
          (p) =>
            !p.includes(".stories") &&
            !p.includes(".test") &&
            !p.endsWith("/index.ts") &&
            !p.endsWith("/index.tsx"),
        )
        .sort((a, b) => a.localeCompare(b))
        .map((p) => ({
          path: p,
          short: p.replace(/^\/src\//, ""),
          name:
            p
              .split("/")
              .pop()
              ?.replace(/\.tsx?$/, "") ?? "",
        }));
    }
  }
  return cachedQuickOpenFiles;
}
